import {FC} from 'react';
import type {AxiosError} from 'axios';
import {useSetSecretCode, useValidatePassword} from '@rocket-ui/lk-api';
import type {AuthResponseSource, AuthTokenResponse} from 'features/auth/model/postAuthRedirect';
import {PasswordInput} from '../PasswordInputForm/PasswordInput';
import {PasswordNumericKeypad} from '../PasswordInputForm/PasswordNumericKeypad';
import {useUserInteraction} from '@rocket-ui/user-interaction';
import {useAutoSubmitCodeLikeInput} from '../useAutoSubmitCodeLikeInput';
import {useIsMaxSm} from '../useIsMaxSm';
import styles from './RepeatPasswordInputForm.module.css';

interface RepeatPasswordInputFormProps {
	token: string;
	password: string;
	confirmWith?: 'validate' | 'recovery';
	onSubmit: (response: AuthTokenResponse, source: AuthResponseSource) => void;
}

const SHAKE_DURATION_MS = 820;
const PIN_LENGTH = 4;
const PASSWORDS_MISMATCH_MESSAGE = 'Пароли не совпадают';

export const RepeatPasswordInputForm: FC<RepeatPasswordInputFormProps> = ({
	token,
	password,
	confirmWith = 'validate',
	onSubmit,
}) => {
	const {notifyUser} = useUserInteraction();
	const {mutateAsync: validatePassword, isLoading: isValidating} = useValidatePassword();
	const {mutateAsync: setRecoverySecretCode, isLoading: isSettingSecretCode} = useSetSecretCode();
	const isLoading = confirmWith === 'recovery' ? isSettingSecretCode : isValidating;
	const isMaxSm = useIsMaxSm();

	const {
		value: code,
		onChange: handleChange,
		errorNonce,
		inputRef,
	} = useAutoSubmitCodeLikeInput({
		requiredLength: PIN_LENGTH,
		clearAfterErrorMs: SHAKE_DURATION_MS,
		notifyUser,
		errorMessageFallback: 'Ошибка при вводе пароля',
		getErrorMessage: (err) => {
			if (err instanceof Error && err.message === PASSWORDS_MISMATCH_MESSAGE) {
				return PASSWORDS_MISMATCH_MESSAGE;
			}

			return (err as AxiosError<{errors?: string[]}> | undefined)?.response?.data?.errors?.[0];
		},
		submit: async (value) => {
			if (value !== password) {
				throw new Error(PASSWORDS_MISMATCH_MESSAGE);
			}

			if (confirmWith === 'recovery') {
				const data = await setRecoverySecretCode({token, code: value});
				onSubmit(data, 'secret_code');
				return;
			}

			const data = await validatePassword({token, password: value});
			onSubmit(data, 'validate');
		},
	});

	return (
		<section className={styles.section}>
			<p className={styles.title}>Повторите пароль</p>

			<div className={styles.inputWrap}>
				<PasswordInput
					value={code}
					onChange={handleChange}
					isLoading={isLoading}
					disabled={isLoading}
					ref={inputRef as React.RefObject<HTMLInputElement>}
					errorNonce={errorNonce}
					suppressNativeKeyboard={isMaxSm}
				/>
			</div>

			<PasswordNumericKeypad
				value={code}
				maxLength={PIN_LENGTH}
				disabled={isLoading}
				onDigit={(digit) => handleChange((code + digit).slice(0, PIN_LENGTH))}
				onBackspace={() => handleChange(code.slice(0, -1))}
			/>
		</section>
	);
};
